import type { ProgressState } from '../model/types.ts';

export function initialProgress(): ProgressState {
  return {
    bestCleared: 0,
    pushStage: 1,
    farmStage: 0,
    streakStage: 0,
    clearStreak: 0,
    failStreak: 0,
    mode: 'push',
    drillStage: 0,
    notice: 'The echo is learning the first stage. Three clears, then it pushes.',
  };
}

export function applyFightResult(
  progress: ProgressState,
  stage: { id: number; isWall: boolean; wallHint?: string },
  victory: boolean,
  lastStageId: number,
): ProgressState {
  const next: ProgressState = { ...progress, notice: null };
  if (victory) {
    next.failStreak = 0;
    next.bestCleared = Math.max(progress.bestCleared, stage.id);
    if (progress.streakStage === stage.id) next.clearStreak = progress.clearStreak + 1;
    else {
      next.streakStage = stage.id;
      next.clearStreak = 1;
    }
    if (next.clearStreak >= 3 && !stage.isWall && progress.drillStage === 0) {
      next.farmStage = Math.max(progress.farmStage, stage.id);
    }
    if (progress.drillStage === stage.id && !stage.isWall) {
      if (next.clearStreak >= 3) {
        next.farmStage = Math.max(progress.farmStage, stage.id);
        next.drillStage = 0;
        next.mode = 'farm';
        next.notice = `The echo will farm stage ${stage.id} while you are away.`;
      } else {
        next.notice = `Drilling stage ${stage.id} · clear ${next.clearStreak}/3`;
      }
      return next;
    }
    const learned = progress.farmStage > 0 || next.clearStreak >= 3;
    if (progress.mode === 'push' && stage.id === progress.pushStage && learned) {
      next.pushStage = Math.min(lastStageId, stage.id + 1);
    }
    if (stage.isWall) {
      if (stage.id === lastStageId) {
        next.notice = 'The wall gives way. Nothing beyond this gate is in the frontier yet.';
        next.mode = 'farm';
      } else {
        next.notice = 'The wall gives way.';
      }
    } else if (next.clearStreak < 3 && progress.farmStage === 0) {
      next.notice = `Learning the stage · clear ${next.clearStreak}/3`;
    } else if (next.farmStage === stage.id && next.clearStreak >= 3) {
      next.notice = `The echo can farm ${stageLabel(stage.id)} while you are away.`;
    }
    return next;
  }

  next.clearStreak = 0;
  next.streakStage = 0;
  next.failStreak = progress.failStreak + 1;
  if (next.failStreak >= 3 && progress.farmStage > 0) {
    next.mode = 'farm';
    next.drillStage = 0;
    next.notice = stage.isWall
      ? (stage.wallHint ?? 'The wall holds. The echo falls back to farm.')
      : 'Three defeats. The echo falls back to a stage it can clear.';
  } else if (next.failStreak >= 3) {
    next.failStreak = 0;
    next.notice = 'No farm stage yet. Adjust the braid and keep at the first fight.';
  } else if (stage.isWall) {
    next.notice = stage.wallHint ?? 'The wall holds.';
  }
  return next;
}

function stageLabel(id: number): string {
  return `stage ${id}`;
}

export function stageToFight(progress: ProgressState): number {
  if (progress.drillStage > 0) return progress.drillStage;
  if (progress.mode === 'farm' && progress.farmStage > 0) return progress.farmStage;
  return progress.pushStage;
}

export function startDrill(progress: ProgressState, stageId: number, isWall: boolean): ProgressState {
  if (isWall || stageId <= 0 || stageId > progress.bestCleared) return progress;
  return {
    ...progress,
    drillStage: stageId,
    streakStage: 0,
    clearStreak: 0,
    failStreak: 0,
    notice: `Drilling stage ${stageId} · clear 0/3`,
  };
}

export function cancelDrill(progress: ProgressState): ProgressState {
  return { ...progress, drillStage: 0, notice: null };
}

export function resumePush(progress: ProgressState): ProgressState {
  return { ...progress, mode: 'push', failStreak: 0, notice: 'Pushing again.' };
}
